import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAudioFiles, uploadAudio, deleteAudioFile } from '../../api/client';
import './AudioFiles.css';

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function AudioFiles() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmName, setConfirmName] = useState(null);
  const [copied, setCopied] = useState(null);
  const fileInputRef = useRef(null);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAudioFiles();
      setFiles(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadAudio(file);
      loadFiles();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (confirmName === filename) {
      try {
        await deleteAudioFile(filename);
        setConfirmName(null);
        loadFiles();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmName(filename);
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  if (loading && files.length === 0) {
    return <div className="loading">Loading audio files...</div>;
  }

  return (
    <div>
      <div className="card">
        <div className="audio-header">
          <h3>Audio Files</h3>
          <div className="audio-upload-area">
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.ogg,.m4a,.webm,.aac"
              onChange={handleUpload}
              style={{ display: 'none' }}
              id="audio-upload"
            />
            <button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Audio'}
            </button>
            <span className="audio-hint">MP3, WAV, OGG, M4A (max 25 MB)</span>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginTop: '0.5rem' }}>{error}</div>
        )}
      </div>

      {files.length === 0 ? (
        <div className="card"><p>No audio files uploaded yet.</p></div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Size</th>
                <th>URL</th>
                <th>Preview</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.filename}>
                  <td style={{ fontWeight: 500 }}>{f.filename}</td>
                  <td>{formatSize(f.size)}</td>
                  <td>
                    <div className="audio-url-cell">
                      <code className="audio-url-text">{f.url}</code>
                      <button
                        className={`btn-secondary-sm ${copied === f.url ? 'copied' : ''}`}
                        onClick={() => handleCopy(f.url)}
                      >
                        {copied === f.url ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </td>
                  <td>
                    <audio controls preload="none" style={{ height: '32px', maxWidth: '200px' }}>
                      <source src={f.url} type="audio/mpeg" />
                    </audio>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {confirmName === f.filename ? (
                      <>
                        <button className="btn-danger-sm" onClick={() => handleDelete(f.filename)}>Confirm</button>
                        {' '}
                        <button className="btn-secondary-sm" onClick={() => setConfirmName(null)}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-icon" onClick={() => handleDelete(f.filename)} title="Delete">&#128465;</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
